import { useCallback, useEffect, useMemo, useState } from 'react';
import type { SelectionDraft, SelectionInfo, SelectionTool } from './types';
import { SELECTION_NAME_MAX } from './selectionForm';

const TOOLS: SelectionTool[] = [
  'add-one', 'add-zone', 'remove-one', 'remove-zone',
];
const NO_MEMBERS: string[] = [];
const INITIAL_SELECTIONS: SelectionInfo[] = [
  { id: 'demo-2', name: 'Vitrolles 2025 - Prospection 1', color: '#0046AF', networkId: '', consumption: 'Chaud' },
  { id: 'demo-3', name: 'Demo 3', color: '#4FB400', networkId: '', consumption: 'Chaud' },
];

/** Local fixtures only; source: buildingIds → validate/resetMode in production. */
export function useSelection(heatById: Record<string, number>) {
  const [selections, setSelections] = useState(INITIAL_SELECTIONS);
  const [selectionId, setSelectionId] = useState<string | null>('demo-2');
  const [membersBySelection, setMembersBySelection] = useState<Record<string, string[]>>({
    'demo-2': ['BFR920720001', 'BFR920720002'],
    'demo-3': ['BFR920720003', 'BFR920720004', 'BFR920720005'],
  });
  const current = selections.find((s) => s.id === selectionId) ?? null;
  const members = (selectionId && membersBySelection[selectionId]) || NO_MEMBERS;
  const selectionLabel = current?.name ?? '';
  const [activeTool, setActiveTool] = useState<SelectionTool | null>(null);
  const [pending, setPending] = useState<string[]>([]);
  const cancel = useCallback(() => { setActiveTool(null); setPending([]); }, []);
  const changeSelection = (id: string) => {
    if (!selections.some((s) => s.id === id)) throw new Error('Sélection inconnue.');
    if (activeTool) return;
    setSelectionId(id);
  };
  /** « Quitter la sélection » : retour à l'accueil sans sélection (goToHome en production). */
  const quit = () => { cancel(); setSelectionId(null); };
  const create = (draft: SelectionDraft) => {
    const name = draft.name.trim();
    if (!name || name.length > SELECTION_NAME_MAX) throw new Error('Nom de sélection invalide.');
    const id = `selection-${crypto.randomUUID()}`;
    setSelections((prev) => [...prev, { ...draft, name, id }]);
    setMembersBySelection((prev) => ({ ...prev, [id]: [] }));
    cancel();
    setSelectionId(id);
    return id;
  };
  useEffect(() => {
    const escape = (e: KeyboardEvent) => { if (e.key === 'Escape') cancel(); };
    window.addEventListener('keydown', escape);
    return () => window.removeEventListener('keydown', escape);
  }, [cancel]);
  const selectTool = (id: string) => {
    if (!selectionId || !TOOLS.includes(id as SelectionTool) || activeTool) return;
    setPending([]);
    setActiveTool(id as SelectionTool);
  };
  const pickBuilding = (id: string) => {
    if (!activeTool) return false;
    if (!(id in heatById)) return true;
    const adding = activeTool.startsWith('add');
    const eligible = (b: string) => adding ? !members.includes(b) : members.includes(b);
    if (activeTool === 'add-one' || activeTool === 'remove-one') {
      if (!eligible(id)) return true;
      setPending((prev) => prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]);
      return true;
    }
    // Zone simulation: the clicked building plus the next eligible ones (max 4), the lasso is not drawn.
    const ids = Object.keys(heatById);
    const start = ids.indexOf(id);
    const zone = [...ids.slice(start), ...ids.slice(0, start)].filter(eligible).slice(0, 4);
    setPending((prev) => [...prev, ...zone.filter((b) => !prev.includes(b))]);
    return true;
  };
  const validate = () => {
    if (!selectionId || !pending.length || !activeTool) return;
    setMembersBySelection((prev) => ({
      ...prev,
      [selectionId]: activeTool.startsWith('add')
        ? [...prev[selectionId], ...pending]
        : prev[selectionId].filter((id) => !pending.includes(id)),
    }));
    cancel();
  };
  const summary = useMemo(() => ({
    buildings: members.length,
    heatTotal: members.reduce((sum, id) => sum + (heatById[id] ?? 0), 0),
    // Network geometry is owned by useNetwork; composed into the panel in index.tsx.
    pipes: 0,
  }), [heatById, members, selectionId]);
  const verb = activeTool?.startsWith('add') ? 'ajouter' : 'retirer';
  const instruction = !activeTool ? null
    : pending.length ? `Valider pour ${verb} vos bâtiments.`
      : activeTool === 'add-one' || activeTool === 'remove-one'
        ? `Cliquez sur les bâtiments ${activeTool === 'add-one' ? 'hors sélection' : 'de la sélection'} sur la carte.`
        : `Cliquer pour sélectionner une zone contenant les bâtiments à ${verb} ${activeTool === 'add-zone' ? 'à' : 'de'} votre sélection.`;
  return { selections, current, selectionId, selectionLabel, changeSelection, quit, create, members, activeTool, pending, cancel, selectTool, pickBuilding, validate, summary, instruction };
}
